// TIBECO Microsoft 365 Integration
import React, { useState } from 'react';
import {
  Mail,
  Calendar,
  Users,
  FileText,
  CheckCircle,
  AlertCircle,
  Settings,
  Zap,
  Cloud,
  Shield,
  RefreshCw,
  Database,
  User,
  Clock,
  Activity
} from 'lucide-react';

const Microsoft365Integration = () => {
  const [connectionStatus] = useState({
    outlook: 'connected',
    calendar: 'connected',
    teams: 'connected',
    sharepoint: 'connected',
    onedrive: 'connected'
  });

  const [stats] = useState({
    emailsSynced: 2847,
    calendarEvents: 156,
    documentsShared: 89,
    teamsMeetings: 34,
    lastSync: '2025-01-15 16:30'
  });

  const getStatusIcon = (status) => {
    return status === 'connected' ? (
      <CheckCircle className="w-5 h-5 text-green-600" />
    ) : (
      <AlertCircle className="w-5 h-5 text-red-600" />
    );
  };

  const services = [
    {
      name: 'Outlook Email',
      icon: Mail,
      status: connectionStatus.outlook,
      description: 'Sincronizare email-uri și contacte',
      count: stats.emailsSynced,
      unit: 'email-uri'
    },
    {
      name: 'Calendar',
      icon: Calendar,
      status: connectionStatus.calendar,
      description: 'Evenimente și întâlniri sincronizate',
      count: stats.calendarEvents,
      unit: 'evenimente'
    },
    {
      name: 'Microsoft Teams',
      icon: Users,
      status: connectionStatus.teams,
      description: 'Chat-uri și meeting-uri Teams',
      count: stats.teamsMeetings,
      unit: 'meeting-uri'
    },
    {
      name: 'SharePoint',
      icon: Database,
      status: connectionStatus.sharepoint,
      description: 'Documente și biblioteci SharePoint',
      count: stats.documentsShared,
      unit: 'documente'
    },
    {
      name: 'OneDrive',
      icon: Cloud,
      status: connectionStatus.onedrive,
      description: 'Stocare și sincronizare fișiere',
      count: '15.2 GB',
      unit: ''
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Mail className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Microsoft 365 Integration
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Conectează TIBECO cu ecosystemul Microsoft 365
              </p>
            </div>
          </div>
        </div>

        {/* Connection Status */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Conectat la Microsoft 365
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Tenant: tibeco.onmicrosoft.com • Ultima sincronizare: {stats.lastSync}
                </p>
              </div>
            </div>
            <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors">
              Reconfigurează
            </button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl mr-3">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {service.name}
                      </h3>
                    </div>
                  </div>
                  {getStatusIcon(service.status)}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {service.description}
                </p>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {typeof service.count === 'number' ? service.count.toLocaleString() : service.count}
                    </p>
                    <p className="text-xs text-gray-500">{service.unit}</p>
                  </div>
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Acțiuni Rapide
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <RefreshCw className="w-6 h-6 text-blue-600 mb-2" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sync Manual</span>
            </button>
            <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <Calendar className="w-6 h-6 text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Calendar</span>
            </button>
            <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <Mail className="w-6 h-6 text-red-600 mb-2" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</span>
            </button>
            <button className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
              <Users className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Teams</span>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Activitate Recentă
            </h3>
            <Activity className="w-5 h-5 text-gray-400" />
          </div>

          <div className="space-y-4">
            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600 mr-3" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Email sincronizat de la client@exemplu.ro
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Acum 5 minute</p>
              </div>
            </div>

            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Calendar className="w-5 h-5 text-green-600 mr-3" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Întâlnire programată: Măsurători Casa Cluj
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Acum 15 minute</p>
              </div>
            </div>

            <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <Users className="w-5 h-5 text-purple-600 mr-3" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Teams meeting finalizat cu echipa instalatori
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Acum 2 ore</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Microsoft365Integration;
